import React, { useState } from "react";
import { Link } from "react-router-dom";
import { axiosPost } from "../apis/useAxios";
import { firstOpenScreen, loadMyPermissions } from "../helpers/permissions";
import { ButtonSkeleton } from "./skeleton/SkeletonElement.js";
import AuthShell, { AuthField } from "./auth/AuthShell";

/** Keys of the browser's local storage: "Remember Me" keeps only the user name (never the password). */
const REMEMBER_USER = "remember_userName";
const OLD_REMEMBER_PASSWORD = "remember_password";

/** The logged-in user kept for the session (sessionStorage), from auth/login. */
export function storeSession(user) {
  sessionStorage.setItem("session_userId", user.id);
  sessionStorage.setItem("session_userTypeId", user.userTypeId);
  sessionStorage.setItem("session_userTypeName", user.userTypeName);
  sessionStorage.setItem("session_userName", user.userName);
  sessionStorage.setItem("session_firstName", user.firstName);
  sessionStorage.setItem("session_lastName", user.lastName);
  sessionStorage.setItem("session_loginUserName", user.loginUserName);
  sessionStorage.setItem("session_email", user.email);
  sessionStorage.setItem("session_phoneNumber", user.phoneNumber);
  sessionStorage.setItem("session_isActive", user.isActive);
  sessionStorage.setItem("session_airlineCode", user.airlineCode);
  sessionStorage.setItem("session_icaoCode", user.icaoCode);
  sessionStorage.setItem("session_sessionId", user.sessionId ?? "");
  // "0" = this user's clicks are not recorded (Users › Activity); pages, logins and saves always are.
  sessionStorage.setItem("session_trackClicks", user.trackClicks === false ? "0" : "1");
}

const startState = () => {
  // A password saved by the old "Remember Me" is removed from the browser.
  localStorage.removeItem(OLD_REMEMBER_PASSWORD);
  const userName = localStorage.getItem(REMEMBER_USER) || "";
  return { userName, password: "", rememberMe: !!userName };
};

/**
 * Login page: auth/login (dbCrewManager users). Inactive users and users outside the allowed networks get the API's
 * message; the first screen the user may view opens after login. Passwords are case-sensitive: Caps Lock is shown.
 */
export function LoginComponent() {
  const [form, setForm] = useState(startState);
  const [errors, setErrors] = useState({ userName: "", password: "" });
  const [message, setMessage] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [capsLock, setCapsLock] = useState(false);

  const change = (e) => {
    const { name, type, checked, value } = e.target; // read now: React 16 reuses the event object
    setForm((f) => ({ ...f, [name === "username" ? "userName" : name]: type === "checkbox" ? checked : value }));
  };
  const watchCaps = (e) => setCapsLock(Boolean(e.getModifierState && e.getModifierState("CapsLock")));

  const login = async () => {
    try {
      const data = await axiosPost("auth/login", { UserName: form.userName.trim(), Password: form.password });
      if (!data?.isSuccess || !data.payload) {
        setSubmitted(false);
        setMessage(data?.message || "Unable to log in.");
        return;
      }
      if (form.rememberMe) {
        localStorage.setItem(REMEMBER_USER, form.userName.trim());
      } else {
        localStorage.removeItem(REMEMBER_USER);
      }
      storeSession(data.payload);
      await loadMyPermissions(); // menus / buttons by the user's access rights
      // The first screen the user may view (Dashboard when allowed); with none, the page explains it.
      window.location.replace(firstOpenScreen()?.route ?? "/dashboard-overview");
    } catch (err) {
      setSubmitted(false);
      setMessage(err?.message || "Unable to log in.");
    }
  };

  const submit = (e) => {
    e.preventDefault();
    const next = {
      userName: form.userName.trim() === "" ? "Please enter username." : "",
      password: form.password === "" ? "Please enter password." : "",
    };
    setErrors(next);
    setMessage("");
    if (next.userName || next.password) return;
    setSubmitted(true);
    login();
  };

  return (
    <AuthShell title="Sign in" hint="Sign in to your account to continue">
      <form method="post" onSubmit={submit} noValidate>
        <AuthField id="userName" label="Username" icon="fas fa-user" error={errors.userName}>
          <input
            type="text"
            className="form-control"
            id="userName"
            name="username"
            placeholder="Enter your username"
            maxLength={75}
            autoComplete="username"
            value={form.userName}
            onChange={change}
            autoFocus
          />
        </AuthField>
        <AuthField
          id="password"
          label="Password"
          icon="fas fa-lock"
          error={errors.password}
          hint={capsLock && <span className="au-caps">Caps Lock is on (passwords are case-sensitive).</span>}
        >
          <input
            id="password"
            type={showPassword ? "text" : "password"}
            className="form-control"
            name="password"
            placeholder="Enter your password"
            maxLength={50}
            autoComplete="current-password"
            value={form.password}
            onChange={change}
            onKeyUp={watchCaps}
            onKeyDown={watchCaps}
          />
          <button
            type="button"
            className="au-eye"
            aria-label={showPassword ? "Hide password" : "Show password"}
            onClick={() => setShowPassword((s) => !s)}
          >
            <i className={showPassword ? "fas fa-eye-slash" : "fas fa-eye"} aria-hidden="true" />
          </button>
        </AuthField>

        {/* Same option card as Hotel Booking Priority's "Add to" choice. */}
        <label className={`au-choice${form.rememberMe ? " is-selected" : ""}`} htmlFor="remember-me">
          <input type="checkbox" name="rememberMe" id="remember-me" checked={form.rememberMe} onChange={change} />
          <i className="fas fa-user-check au-choice__icon" aria-hidden="true" />
          <span className="au-choice__text">
            <span className="au-choice__label">Remember me</span>
            <span className="au-choice__hint">Keep my user name on this computer · your browser can save the password</span>
          </span>
          <i className="fas fa-check-circle au-choice__check" aria-hidden="true" />
        </label>
        <div className="au-row au-row--end">
          <Link to="/forgot-password" className="au-forgot">
            <i className="fas fa-key mr-1" aria-hidden="true" />
            Forgot password?
          </Link>
        </div>

        {message && (
          <div className="alert alert-danger au-alert" role="alert">
            <i className="fas fa-exclamation-circle mr-2" aria-hidden="true" />
            {message}
          </div>
        )}

        {submitted ? (
          <ButtonSkeleton
            className="btn btn-primary btn-lg btn-block"
            iconClassName="fas fa-sign-in-alt mr-2"
            iconFontSize="20px"
            text="Login"
            showDotedLoader
            dotedLoaderMarginTop="10px"
          />
        ) : (
          <button type="submit" className="btn btn-primary btn-lg btn-block">
            <i className="fas fa-sign-in-alt mr-2" aria-hidden="true" />
            Login
          </button>
        )}
      </form>
    </AuthShell>
  );
}

export default LoginComponent;
